import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminUser } from '@couch/shared';
import { accountApi } from '../../api/account';
import { api, errorMessage } from '../../api/client';

const USERS_KEY = ['users'] as const;

const JOINED: Readonly<Record<AdminUser['joinedVia'], string>> = Object.freeze({ setup: 'Owner', invite: 'Invite', signup: 'Sign-up' });

/** Give full space / Limit to the sign-up size (doc 65). */
function SpaceButton({ user }: { user: AdminUser }) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: () => api.setPreviewSpace(user.id, user.fullSpace),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
  if (user.role === 'owner') return null;
  return (
    <>
      <button type="button" className="button-quiet" aria-label={`${user.fullSpace ? 'Limit previews of' : 'Give full space to'} ${user.username}`} disabled={change.isPending} onClick={() => { change.mutate(); }}>
        {user.fullSpace ? 'Limit previews' : 'Give full space'}
      </button>
      {change.isError && <span role="alert" className="form-error">{errorMessage(change.error)}</span>}
    </>
  );
}

/** Delete another account (doc 66); never the owner. */
function DeleteButton({ user }: { user: AdminUser }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => accountApi.deleteUser(user.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
  if (user.role === 'owner') return null;
  const confirm = () => {
    const text = `Delete ${user.username} and everything of theirs in Couch? Their machines stop working with Couch. Cloud machines in their own RunPod account keep running there. This cannot be undone.`;
    if (window.confirm(text)) remove.mutate();
  };
  return (
    <>
      <button type="button" className="button-quiet" aria-label={`Delete ${user.username}`} disabled={remove.isPending} onClick={confirm}>Delete</button>
      {remove.isError && <span role="alert" className="form-error">{errorMessage(remove.error)}</span>}
    </>
  );
}

function UserRow({ user }: { user: AdminUser }) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: () => api.setUserDisabled(user.id, !user.disabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });
  const action = user.disabled ? 'Enable' : 'Disable';
  return (
    <tr>
      <td>{user.displayName}</td>
      <td>{user.username}</td>
      <td>{user.role}</td>
      <td>{JOINED[user.joinedVia]}</td>
      <td>{user.previewMb} MB</td>
      <td><span className={`badge ${user.disabled ? 'badge-revoked' : 'badge-pending'}`}>{user.disabled ? 'disabled' : 'active'}</span></td>
      <td>
        <SpaceButton user={user} />
        <DeleteButton user={user} />
        {user.role !== 'owner' && (
          <button type="button" className="button-quiet" aria-label={`${action} ${user.username}`} disabled={toggle.isPending} onClick={() => { toggle.mutate(); }}>{action}</button>
        )}
        {toggle.isError && <span role="alert" className="form-error">{errorMessage(toggle.error)}</span>}
      </td>
    </tr>
  );
}

export function UsersPanel() {
  const users = useQuery({ queryKey: USERS_KEY, queryFn: api.listUsers });
  return (
    <section className="panel" aria-labelledby="users-title">
      <h2 id="users-title">Users</h2>
      {users.isPending && <p role="status">Loading users…</p>}
      {users.isError && <p role="alert" className="form-error">{errorMessage(users.error)}</p>}
      {users.isSuccess && (
        <div className="table-scroll">
          <table>
            <thead><tr><th scope="col">Name</th><th scope="col">Username</th><th scope="col">Role</th><th scope="col">Joined by</th><th scope="col">Previews</th><th scope="col">Status</th><th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
            <tbody>{users.data.users.map((u) => <UserRow key={u.id} user={u} />)}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
